import { getScalarSpellProgressionMaxCastLevel, isManaSpellSystem } from './spellSystem';
import {
  SPELL_BINDING_SURFACE_SHEET,
  getBoundResourceState,
  getSpellResourceBinding,
  quoteBoundResourceSpend,
} from '../systems/resourceEngine';

export function calculateMaxMana(spellSlots = {}) {
  let total = 0;
  Object.entries(spellSlots || {}).forEach(([level, count]) => {
    total += parseInt(level, 10) * count;
  });
  return total;
}

export function getMaxSpellLevel(spellSlots = {}) {
  const levels = Object.keys(spellSlots || {}).map(Number).filter((level) => (spellSlots?.[level] || 0) > 0);
  return levels.length > 0 ? Math.max(...levels) : 0;
}

export function calculateManaCost(spellLevel, maxSpellLevel, options = {}) {
  const allowOvercast = options.allowOvercast !== false;
  if (spellLevel === 0) return { cost: 0, free: true, overcast: false };

  if (maxSpellLevel - spellLevel >= 3) {
    return { cost: 0, free: true, overcast: false, freecast: true };
  }

  if (spellLevel > maxSpellLevel) {
    if (allowOvercast && spellLevel <= maxSpellLevel + 2) {
      return { cost: spellLevel * 2, free: false, overcast: true };
    }
    return { cost: Infinity, free: false, overcast: true, tooHigh: true };
  }

  return { cost: spellLevel, free: false, overcast: false };
}

function getLegacyLevelOptions(spell, maxSpellLevel, overcastEnabled) {
  const maxCastLevel = Math.max(
    spell.level || 0,
    Math.min(9, maxSpellLevel + (overcastEnabled ? 2 : 0))
  );
  return Array.from(
    { length: Math.max(1, maxCastLevel - (spell.level || 0) + 1) },
    (_, index) => (spell.level || 0) + index
  );
}

function getTieredLevelOptions(spell, state) {
  if ((spell.level || 0) === 0) return [0];
  if (!state?.available || !Array.isArray(state.tiers)) {
    return [spell.level || 0];
  }

  const levels = state.tiers
    .map((tier) => tier.level)
    .filter((level) => level >= (spell.level || 0))
    .sort((left, right) => left - right);

  return levels.length > 0 ? Array.from(new Set(levels)) : [spell.level || 0];
}

export function getSpellCastInfo({
  char,
  spell,
  systems = {},
  settings = {},
  surface = SPELL_BINDING_SURFACE_SHEET,
  castLevel = null,
  spellSlots = {},
  usedSlots = {},
  currentMana = 0,
  maxSpellLevel = 0,
  freeCastsUsedToday = 0,
  freeCastsPerDay = 4,
  manualCostOverride = null,
} = {}) {
  const resolvedCastLevel = Number.isFinite(castLevel) ? castLevel : (spell?.level || 0);
  const binding = getSpellResourceBinding({
    char,
    spell,
    systems,
    surface,
  });
  const overcastEnabled = settings?.overcastEnabled !== false;
  const scalarMaxSpellLevel = getScalarSpellProgressionMaxCastLevel(settings, maxSpellLevel) ?? 0;

  if (binding.kind === 'system-tiered') {
    const state = getBoundResourceState(char, systems, binding);
    const levelOptions = getTieredLevelOptions(spell, state);
    if ((spell?.level || 0) === 0) {
      return {
        binding,
        state,
        kind: 'system-tiered',
        displayMode: 'system-tiered',
        castLevel: resolvedCastLevel,
        availableCastLevels: levelOptions,
        cost: 0,
        canCast: true,
        freeAvailable: true,
        overcast: false,
        tooHigh: false,
        slotsRemaining: null,
        quote: null,
        resourceLabel: state.label || 'Slots',
      };
    }

    const selectedTier = state.available ? state.tiers.find((tier) => tier.level === resolvedCastLevel) || null : null;
    const quote = quoteBoundResourceSpend(char, systems, binding, { castLevel: resolvedCastLevel });
    const maxTierLevel = levelOptions.length > 0 ? Math.max(...levelOptions) : (spell?.level || 0);

    return {
      binding,
      state,
      kind: 'system-tiered',
      displayMode: 'system-tiered',
      castLevel: resolvedCastLevel,
      availableCastLevels: levelOptions,
      cost: quote?.ok ? quote.amount : 1,
      canCast: quote.ok,
      freeAvailable: false,
      overcast: false,
      tooHigh: !selectedTier && resolvedCastLevel > maxTierLevel,
      slotsRemaining: selectedTier?.current ?? 0,
      quote,
      resourceLabel: state.label || 'Slots',
    };
  }

  if (binding.kind === 'system-scalar') {
    const state = getBoundResourceState(char, systems, binding);
    const derivedInfo = calculateManaCost(resolvedCastLevel, scalarMaxSpellLevel, { allowOvercast: overcastEnabled });
    const isBaseCast = resolvedCastLevel === (spell?.level || 0);
    const freeAvailable = !!derivedInfo.freecast && isBaseCast && freeCastsUsedToday < freeCastsPerDay;
    const quote = resolvedCastLevel === 0
      ? null
      : quoteBoundResourceSpend(char, systems, binding, {
          castLevel: resolvedCastLevel,
          derivedCost: freeAvailable ? 0 : derivedInfo.cost,
        });

    return {
      binding,
      state,
      kind: 'system-scalar',
      displayMode: 'system-scalar',
      castLevel: resolvedCastLevel,
      availableCastLevels: getLegacyLevelOptions(spell, scalarMaxSpellLevel, overcastEnabled),
      cost: resolvedCastLevel === 0 ? 0 : (quote?.amount ?? (freeAvailable ? 0 : derivedInfo.cost)),
      canCast: resolvedCastLevel === 0 ? true : !!quote?.ok,
      freeAvailable,
      overcast: !!derivedInfo.overcast,
      tooHigh: !!derivedInfo.tooHigh,
      droughtWarning: false,
      quote,
      resourceLabel: state.label || 'Resource',
      consumesFreeCast: freeAvailable,
    };
  }

  if (isManaSpellSystem(settings)) {
    const derivedInfo = calculateManaCost(resolvedCastLevel, scalarMaxSpellLevel, { allowOvercast: overcastEnabled });
    const isBaseCast = resolvedCastLevel === (spell?.level || 0);
    const freeAvailable = !!derivedInfo.freecast && isBaseCast && freeCastsUsedToday < freeCastsPerDay;
    const effectiveCost = freeAvailable
      ? 0
      : (Number.isFinite(manualCostOverride) ? Math.max(0, manualCostOverride) : derivedInfo.cost);
    return {
      binding,
      kind: 'legacy-mana',
      displayMode: 'legacy-mana',
      castLevel: resolvedCastLevel,
      availableCastLevels: getLegacyLevelOptions(spell, scalarMaxSpellLevel, overcastEnabled),
      cost: effectiveCost,
      canCast: effectiveCost <= currentMana || freeAvailable || ((char?.manaDrought ?? false) && resolvedCastLevel <= 3),
      freeAvailable,
      overcast: !!derivedInfo.overcast,
      tooHigh: !!derivedInfo.tooHigh,
      droughtWarning: effectiveCost > currentMana && resolvedCastLevel <= 3,
      resourceLabel: 'MP',
    };
  }

  const slotsRemaining = resolvedCastLevel === 0
    ? Infinity
    : ((spellSlots?.[resolvedCastLevel] || 0) - (usedSlots?.[resolvedCastLevel] || 0));

  return {
    binding,
    kind: 'legacy-slots',
    displayMode: 'legacy-slots',
    castLevel: resolvedCastLevel,
    availableCastLevels: getLegacyLevelOptions(spell, maxSpellLevel, overcastEnabled),
    cost: resolvedCastLevel === 0 ? 0 : 1,
    canCast: resolvedCastLevel === 0 || slotsRemaining > 0,
    freeAvailable: resolvedCastLevel === 0,
    overcast: false,
    tooHigh: false,
    slotsRemaining,
    resourceLabel: 'Slots',
  };
}
